import { ref, watch, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useWallet } from '@solana/wallet-adapter-vue'
import { useUserStore } from '@/stores/userStore'
import { DEV_CONFIG } from '@/config/dev'
import {
  requestWalletSignature,
  clearSignature,
  DISCLAIMER_VERSION
} from '@/utils/walletSignature'

// Configuration for wallet verification behavior
const CONFIG = {
  // Whether to verify wallet on tab focus return
  // Set to true for higher security, false for better UX
  verifyOnTabFocus: false,

  // Minimum time between tab focus verifications (ms)
  // Prevents spamming user with prompts
  tabFocusVerifyInterval: 5 * 60 * 1000, // 5 minutes
}

/**
 * Composable to handle wallet connection integration
 * Bridges the wallet adapter with the user store
 * Supports both mock mode and real wallet connections
 */
export function useWalletConnection() {
  const userStore = useUserStore()
  const router = useRouter()
  const showWalletModal = ref(false)
  const lastTabFocusVerify = ref(0)

  // Get wallet adapter instance
  const walletAdapter = DEV_CONFIG.useMockData && DEV_CONFIG.mockWalletConnection
    ? null
    : useWallet()

  /**
   * Handles tab visibility change - optionally verify wallet on return
   */
  function handleVisibilityChange() {
    if (document.hidden) return
    if (!userStore.walletConnected) return
    if (!CONFIG.verifyOnTabFocus) return

    // Check if enough time has passed since last verification
    const now = Date.now()
    if (now - lastTabFocusVerify.value < CONFIG.tabFocusVerifyInterval) {
      return
    }

    lastTabFocusVerify.value = now

    // Verify wallet - same behavior as action verification
    // User rejection: stays on page
    // Wallet locked: redirects to home
    verifyBeforeAction()
  }

  // Set up visibility change listener for tab focus detection
  if (CONFIG.verifyOnTabFocus) {
    onMounted(() => {
      document.addEventListener('visibilitychange', handleVisibilityChange)
    })
  }

  /**
   * Opens the wallet selection modal
   */
  function openWalletModal() {
    // Clear any previous errors
    userStore.error = null

    if (DEV_CONFIG.useMockData && DEV_CONFIG.mockWalletConnection) {
      // In mock mode, simulate showing modal then auto-connect
      showWalletModal.value = true

      // Auto-close after 2 seconds and connect with mock data
      setTimeout(async () => {
        showWalletModal.value = false
        await userStore.connectWallet(true) // true = has NFT
      }, 2000)
    } else {
      // Real mode: show wallet selection modal
      showWalletModal.value = true
    }
  }

  /**
   * Closes the wallet selection modal
   */
  function closeWalletModal() {
    showWalletModal.value = false
  }

  /**
   * Handles wallet selection from modal
   */
  async function handleWalletSelected(walletInfo) {
    if (!DEV_CONFIG.useMockData || !DEV_CONFIG.mockWalletConnection) {
      // Real wallet connection
      try {
        // Extract wallet info
        const { publicKey, wallet } = walletInfo

        if (publicKey) {
          const walletAddressStr = publicKey.toString()

          // Store wallet address but DON'T set connected yet (wait for signature check)
          userStore.walletAddress = walletAddressStr

          // Check if this wallet needs to sign disclaimer (check database)
          let needsSignature = true
          try {
            const response = await fetch(`/.netlify/functions/check-signature?wallet=${walletAddressStr}`)
            if (response.ok) {
              const data = await response.json()
              needsSignature = data.needsSignature
            }
          } catch (checkErr) {
            // Could not check database, will request signature
          }

          if (needsSignature) {
            try {
              // Request signature from wallet
              const signatureData = await requestWalletSignature(wallet, walletAddressStr)

              // Store signature in database
              try {
                await fetch('/.netlify/functions/check-signature', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    wallet: walletAddressStr,
                    signature: signatureData.signature,
                    version: signatureData.version
                  })
                })
              } catch (storeErr) {
                // Could not store in database, continue anyway
              }
            } catch (signError) {
              userStore.error = 'You must sign the disclaimer to use Pythentity'
              userStore.walletAddress = null
              userStore.currentUser = null
              closeWalletModal()
              return
            }
          }

          // NOW set connected to true (after signature check/verification)
          userStore.walletConnected = true

          // Fetch user data from backend (or mock data)
          if (!DEV_CONFIG.useMockData) {
            await userStore.fetchUserData()
          } else {
            // In mock mode, just set mock user data
            await userStore.connectWallet(true) // true = has NFT (mock)
          }

          // Redirect to profile if user has NFT (both new and returning users)
          if (userStore.hasNFT) {
            router.push('/profile')
          }
        }
      } catch (error) {
        userStore.error = error.message
      }
    }

    closeWalletModal()
  }

  /**
   * Verifies wallet is unlocked before performing an action.
   * Call this before any sensitive operation.
   *
   * Behavior is determined by verifyWalletUnlocked():
   * - User rejection: returns false, user stays on page
   * - Wallet locked: handleWalletLocked() called, redirects to home
   *
   * @returns {Promise<boolean>}
   */
  async function verifyBeforeAction() {
    if (!userStore.walletConnected || !walletAdapter?.publicKey?.value) {
      return false
    }

    const verified = await userStore.verifyWalletUnlocked(
      walletAdapter,
      userStore.walletAddress
    )

    // If wallet was locked, handleWalletLocked() was called and cleared state
    // Check if we need to redirect
    if (!verified && !userStore.walletConnected) {
      router.push('/')
    }

    return verified
  }

  /**
   * Disconnects the wallet
   */
  async function handleDisconnect() {
    if (DEV_CONFIG.useMockData && DEV_CONFIG.mockWalletConnection) {
      // Mock disconnect
      await userStore.disconnectWallet()
      // Redirect to home page
      router.push('/')
    } else {
      // Real disconnect
      if (walletAdapter) {
        await walletAdapter.disconnect()
        await userStore.disconnectWallet()
        // Note: The watch function will handle the redirect for real wallet disconnections
      }
    }
  }

  // Watch for wallet adapter changes (real mode only)
  if (walletAdapter) {
    // Watch for wallet connection/disconnection (lock detection)
    watch(
      () => walletAdapter.publicKey?.value,
      async (newPublicKey, oldPublicKey) => {
        if (newPublicKey) {
          userStore.walletAddress = newPublicKey.toString()
          userStore.walletConnected = true

          // Only fetch user data if not already loaded (e.g. by session restore)
          if (!DEV_CONFIG.useMockData && !userStore.currentUser) {
            await userStore.fetchUserData()
          }
        } else if (oldPublicKey) {
          // Wallet disconnected/locked - clear ALL state
          const walletAddressStr = oldPublicKey.toString()

          userStore.walletAddress = null
          userStore.walletConnected = false
          userStore.currentUser = null
          userStore.isAuthenticated = false
          userStore.error = null

          // Clear localStorage
          userStore.clearLocalStorage?.()

          // Clear wallet signature - must re-sign on next connection
          clearSignature(walletAddressStr)

          // Redirect to home page
          router.push('/')
        }
      },
      { immediate: false } // Don't run on initial setup
    )

    // Watch for wallet errors (only if error property exists)
    if (walletAdapter.error) {
      watch(
        () => walletAdapter.error?.value,
        (error) => {
          if (error) {
            userStore.error = error.message
          }
        }
      )
    }
  }

  // Cleanup on component unmount
  onUnmounted(() => {
    document.removeEventListener('visibilitychange', handleVisibilityChange)
  })

  return {
    showWalletModal,
    openWalletModal,
    closeWalletModal,
    handleWalletSelected,
    handleDisconnect,
    verifyBeforeAction,
  }
}
